import {
    type ProductEntry,
    productEvent,
    type ProductFlow,
    ProductJourney
} from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { useWalletHomeAnalytics } from "@shared/analytics/useWalletHomeAnalytics";
import { useUsdvBalanceForAddress } from "@shared/wallet/hooks/useUsdvBalanceForAddress";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import {
    formatCurrency,
    useSessionStore,
    useTokenBalancesActions,
    useTokenBalancesData,
    type YaasPositionCardData,
    type YaasSavedDestination
} from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { AnimatePresence, motion } from "motion/react";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { AppFootnote } from "@/components/AppFootnote";
import { AccountTransactionHistory } from "@/components/Data/AccountTransactionHistory";
import { retailMinBalance } from "@/config/retailYaasPolicy";
import { useReportingLiveApy } from "@/hooks/stats/useReportingLiveApy";
import {
    useDistributionMode,
    useNextDistribution,
    useYaasSavedDestinations,
    useYaasWalletData
} from "@/hooks/yaas";
import { withPositionAmount } from "@/hooks/yaas/helpers";
import { useRetailAccount } from "@/hooks/yaas/useRetailAccount";
import { useRetailLpPositions } from "@/hooks/yaas/useRetailLpPositions";

import type { DistributionMode } from "./application/types";
import { VerificationConsentModal } from "./application/VerificationConsentModal";
import { RetailYaasComparisonExplorer } from "./components/RetailYaasComparisonExplorer";
import { YaasPositionsFooter } from "./components/YaasPositionsFooter";
import { useYaasRetailFlow } from "./flow/useYaasRetailFlow";
import { balancePositions } from "./helpers/balancePositions";
import { breakdownTitleFor } from "./helpers/breakdownDisplay";
import { readDemoDistributionMode, readDemoState } from "./helpers/demoState";
import { isRetailSelfHeldPosition } from "./helpers/isRetailSelfHeldPosition";
import { positionTotal } from "./helpers/positionTotal";
import {
    qualifyingAmount,
    type RetailBalanceTotal,
    retailBalanceTotal
} from "./helpers/retailBalanceTotal";
import { retailPositionState } from "./helpers/retailPositionState";
import { MOCK_CONNECTED_WALLET } from "./mocks/retailMocks";
import { AccountUnavailablePanel } from "./sections/AccountUnavailablePanel";
import { BuyUsdvPanel } from "./sections/BuyUsdvPanel";
import { ConfigureRoutingPanel } from "./sections/ConfigureRoutingPanel";
import { EnableRewardsForm } from "./sections/EnableRewardsForm";
import { LeftHero } from "./sections/LeftHero";
import { PerPositionRoutingPanel } from "./sections/PerPositionRoutingPanel";
import { PositionsSection } from "./sections/PositionsSection";
import { PreviewLanding } from "./sections/PreviewLanding";
import { SimBadge } from "./sections/SimBadge";
import { SwitchDistributionModePanel } from "./sections/SwitchDistributionModePanel";
import type { SharedDestination } from "./state/types";
import { useResolvedRetailView } from "./state/useResolvedRetailView";

// ---------------------------------------------------------------------------
// Page — 60/40 split mirroring USDvPage. Left = guilloche + portfolio
// summary (or hero text when no position). Right = state-aware action
// card (Connect / Apply / Position).
// ---------------------------------------------------------------------------

function toSharedDestination(
    destination: YaasSavedDestination
): SharedDestination {
    return {
        id: destination.uuid,
        uuid: destination.uuid,
        label: destination.name,
        address: destination.address,
        percentage: 0,
        type: "wallet"
    };
}

/** Sim and `?state=` frames total their fixture positions, never live reads. */
function demoBalanceTotal(
    positions: YaasPositionCardData[]
): RetailBalanceTotal | null {
    const wallet = positions.find(({ pool }) => isRetailSelfHeldPosition(pool));
    return retailBalanceTotal(
        positions,
        String(wallet?.position.USDvValue ?? 0),
        [],
        false
    );
}

export function RetailYaasLandingPage(): React.ReactElement {
    const selectedAccount = useRetailAccount();
    const vaultBalance = useUsdvBalanceForAddress(
        selectedAccount.isSquads && selectedAccount.ready
            ? selectedAccount.vaultAddress
            : null
    );
    const demoState = useMemo(() => readDemoState(), []);
    // `?mode=stream|claim` pins the mode for a demo frame — see
    // readDemoDistributionMode. Null on every non-demo visit.
    const demoDistributionMode = useMemo(() => readDemoDistributionMode(), []);

    const { apy } = useReportingLiveApy();
    const averageAPY = apy !== null ? String(apy) : null;

    const { setYaasStatus } = useSessionStore();
    // Wallet info + form-phase machine all flow through useYaasRetailFlow.
    // Real mode passes through to `useWallet` / `useWalletModal`; sim mode
    // (`?simulate=enroll|connect|full|short`) overrides the wallet with a
    // self-contained simulated state and runs the full transition pipeline
    // on real timeouts.
    const flow = useYaasRetailFlow();
    const walletAddress = flow.walletAddress;
    const productJourneys = useRef<
        Partial<Record<ProductFlow, ProductJourney>>
    >({});
    const requestProductFlow = (
        productFlow: ProductFlow,
        entry: ProductEntry
    ): void => {
        const journey = new ProductJourney(
            productFlow,
            entry,
            walletAddress || undefined
        );
        productJourneys.current[productFlow] = journey;
        journey.send("requested");
    };

    const wallet = useYaasWalletData();
    const nextDistribution = useNextDistribution();
    const { yaasStatus } = wallet;
    const distributionModeApi = useDistributionMode();

    // SPL token balances — drives the connected-no-yaas landing variant
    // (BuyWidget vs YaasEarnProjectionCTA). Null per-token values mean
    // the wallet doesn't hold that mint; we coerce to 0 for the variant
    // math. `isLoading` is consulted so we don't flash the BuyWidget for
    // users who do hold USDv but whose accounts haven't been fetched yet.
    const {
        balances,
        isLoading: personalBalancesLoading,
        error: personalBalanceError,
        lastFetchedAt
    } = useTokenBalancesData();
    const { refetch: retryPersonalBalances } = useTokenBalancesActions();
    const retryBalances = selectedAccount.isSquads
        ? vaultBalance.refetch
        : retryPersonalBalances;
    const isBalancesLoading = selectedAccount.isSquads
        ? !selectedAccount.ready || vaultBalance.isLoading
        : personalBalancesLoading;
    const balanceError = selectedAccount.isSquads
        ? (selectedAccount.notice ?? vaultBalance.error?.message ?? null)
        : personalBalanceError;
    // A refresh after the first fetch keeps the balances we already have, so
    // the page must not fall back to its loading shape and remount the widget.
    const hasLoadedBalances = selectedAccount.isSquads
        ? selectedAccount.ready && vaultBalance.hasFetched
        : lastFetchedAt !== null;
    const stablesBalance = selectedAccount.isSquads
        ? 0
        : (balances.usdc ?? new BigDecimal(0))
              .add(balances.usdt ?? new BigDecimal(0))
              .toNumber();

    const lpHoldings = useRetailLpPositions(
        selectedAccount.vaultAddress,
        wallet.positions,
        selectedAccount.ready && !flow.isSimulated && demoState === null,
        undefined,
        selectedAccount.address
    );
    const isFirstBalancesLoad = isBalancesLoading && !hasLoadedBalances;
    const usdvBalanceSource = selectedAccount.isSquads
        ? vaultBalance.amount
        : balances.usdv;
    const usdvBalanceExact = useMemo(
        () => usdvBalanceSource ?? new BigDecimal(0),
        [usdvBalanceSource]
    );
    const usdvBalance = usdvBalanceExact.toNumber();
    const walletBalanceAmount =
        hasLoadedBalances && !balanceError ? usdvBalanceExact.toString() : null;

    // Overlay the Helius USDv balance onto retail TREASURY positions.
    // The backend's position.amount can lag the wallet's current balance.
    const lpWalletPositions = lpHoldings.positions;
    const overlaidWalletPositions = useMemo(() => {
        if (isFirstBalancesLoad) return lpWalletPositions;
        return lpWalletPositions.map((position) => {
            if (!isRetailSelfHeldPosition(position.pool)) return position;
            return withPositionAmount(position, usdvBalanceExact);
        });
    }, [lpWalletPositions, usdvBalanceExact, isFirstBalancesLoad]);
    // The one combined total: Balances shows it, and eligibility reads it.
    const balanceTotal = retailBalanceTotal(
        overlaidWalletPositions,
        walletBalanceAmount,
        lpHoldings.holdings,
        lpHoldings.balancesUnavailable
    );
    // Eligibility also waits for the account and its configured minimum.
    const qualifyingBalance =
        wallet.isLoading || wallet.error !== null
            ? null
            : qualifyingAmount(
                  balanceTotal,
                  retailMinBalance(wallet.minBalance)
              );
    const overlaidWalletStats = useMemo(() => {
        if (!wallet.walletStats || isFirstBalancesLoad)
            return wallet.walletStats;
        // Reward projections use backend positions; fallback holdings belong only in Balances.
        const total = positionTotal(balancePositions(overlaidWalletPositions));
        return {
            ...wallet.walletStats,
            totalPositions: total?.toNumber() ?? 0,
            totalPositionsExact: total,
            formattedTotalPositions:
                total === null ? "—" : formatCurrency(total)
        };
    }, [wallet.walletStats, overlaidWalletPositions, isFirstBalancesLoad]);

    useEffect(() => {
        if (!selectedAccount.isSquads && yaasStatus !== null)
            setYaasStatus(yaasStatus);
    }, [yaasStatus, setYaasStatus, selectedAccount.isSquads]);

    const handleBuyUSDv = (): void => {
        if (selectedAccount.isSquads) {
            // A multisig acquires USDv in Squads itself: open the vault's
            // trade page rather than the in-app swap.
            window.open(
                `https://app.squads.so/squads/${selectedAccount.vaultAddress}/trade`,
                "_blank",
                "noopener,noreferrer"
            );
            return;
        }
        requestProductFlow("swap", "rewards_entry");
        setIsBuyingUSDv(true);
    };

    // ─── Resolved view state ────────────────────────────────────────
    // Single object describing what the page should render right now.
    // Owns the 4-priority resolution tree (sim → flow interactive →
    // static demo → real default) — see useResolvedRetailView.ANATOMY.md.
    const resolved = useResolvedRetailView({
        flow,
        walletAddress,
        demoState,
        wallet,
        overlaidWalletPositions,
        overlaidWalletStats,
        walletBalanceExact:
            walletBalanceAmount === null ? null : usdvBalanceExact,
        qualifyingBalance,
        stablesBalance,
        isBalancesLoading,
        nextDistribution,
        isSquads: selectedAccount.isSquads
    });
    // Real-ceremony context — sim frames and `?state=` demos keep their
    // local-state behavior; only real connected wallets hit yaas-api.
    const canSubmitRealActions =
        !flow.isSimulated &&
        demoState === null &&
        resolved.isWalletConnected &&
        !!walletAddress;
    // Opening a routing editor is local UI; only SUBMITTING one touches
    // yaas-api. Keeping those separate lets `?state=` frames and sims
    // demonstrate the Update → editor interaction (and what Advanced
    // Mode changes about it) with no way for a demo to fire a real
    // mutation — the submit gate below is unchanged.
    const canEditDestinations =
        FEATURE_FLAGS.retailPositionRouting && !selectedAccount.isSquads;
    const canSubmitDestinations = canEditDestinations && canSubmitRealActions;

    const { primaryPosition, isPositionDisabled, isBelowMinimum } =
        retailPositionState({
            positions: resolved.positions,
            qualifyingBalance:
                flow.isSimulated || demoState !== null
                    ? positionTotal(resolved.positions)
                    : qualifyingBalance,
            minBalance: resolved.minBalanceExact,
            disabledByUser: resolved.disabledByUser
        });
    const eligibilityNotice =
        selectedAccount.notice ??
        (flow.isSimulated || demoState !== null
            ? null
            : qualifyingBalance === null
              ? "Unable to verify the combined USDv balance yet. Wait for balances to load or retry."
              : qualifyingBalance.lt(resolved.minBalanceExact)
                ? `Hold at least ${resolved.minBalanceExact.toString()} USDv across your wallet and eligible LP positions to enable rewards.`
                : null);
    const hasPosition = primaryPosition !== null;
    const savedDestinations = useYaasSavedDestinations(
        hasPosition ? walletAddress : null
    );
    const rewardsEnabledAccount =
        !hasPosition &&
        (flow.successfulAccount ?? wallet.account)?.status === "Active"
            ? (flow.successfulAccount ?? wallet.account)
            : null;
    const accountUnavailableStatus =
        flow.unavailableAccountStatus ??
        (wallet.account?.status !== "Active" ? wallet.account?.status : null);
    const [exploreOpen, setExploreOpen] = useState(false);
    const [distributionsOpen, setDistributionsOpen] = useState(false);
    const previousAnalyticsAddress = useRef(walletAddress);
    useEffect(() => {
        if (previousAnalyticsAddress.current === walletAddress) return;
        previousAnalyticsAddress.current = walletAddress;
        for (const journey of Object.values(productJourneys.current))
            journey.close("wallet_changed");
        productJourneys.current = {};
        setExploreOpen(false);
        setDistributionsOpen(false);
        setIsBuyingUSDv(false);
        setIsSwitchingMode(false);
        setIsConfiguringRouting(false);
        setRoutingPositionSource(null);
        setInlineEditPosition(null);
    }, [walletAddress]);
    useEffect(() => setDistributionsOpen(false), [walletAddress]);
    // When true, the right column (normally YaasEarnProjectionCTA) swaps
    // to the inline BuyWidget so the user can buy more USDv without
    // leaving the YaaS landing. Set by the "Get More USDv" CTA;
    // cleared by the back affordance above the widget.
    const [isBuyingUSDv, setIsBuyingUSDv] = useState(false);
    // Fires 3s after a swap confirms, from whichever surface hosted the
    // widget. Both moves are unconditional because the page's own ternary
    // already encodes where each wallet belongs: leaving the buy panel
    // falls through to Positions for an earning wallet and to the earn
    // projection for one that now holds USDv, and the refreshed balance
    // is what flips `computeLandingVariant` off the swap variants for the
    // inline widget that has no panel to leave.
    const handleSwapComplete = useCallback((): void => {
        void retryBalances();
        setIsBuyingUSDv(false);
    }, [retryBalances]);
    // Routing/Destinations management — the full PanelMenu + Rewards
    // Routing percentage table + Destination Accounts list surface.
    // Lives behind the "Configure rewards routing" footer link so
    // users only opt into the heavier UI when they actually need
    // multi-destination routing.
    const [isConfiguringRouting, setIsConfiguringRouting] = useState(false);
    // User's current committed distribution mode. Source of truth for
    // the footer's Switch-mode toggle text. Flipped only when the
    // user confirms a switch via Verify Ownership — clicking the
    // footer link opens the Switch Distribution form (below) with the
    // opposite mode pre-selected; the commit step is what writes
    // back to this state. Defaults to "stream" (typical active state).
    const [manageDistributionMode, setManageDistributionMode] =
        useState<DistributionMode>(demoDistributionMode ?? "stream");
    // Adopt the server's committed mode once the dashboard loads — the
    // local "stream" default is only a pre-fetch placeholder.
    const serverDistributionMode =
        wallet.distributionMode ??
        flow.successfulAccount?.distributionMode ??
        null;
    useEffect(() => {
        // A `?mode=` demo frame outranks the server: the frame exists to
        // show one mode, and adopting a real account's mode would swap it
        // out from under the screenshot.
        if (demoDistributionMode !== null) return;
        if (serverDistributionMode !== null) {
            setManageDistributionMode(serverDistributionMode);
        }
    }, [serverDistributionMode, demoDistributionMode]);
    // Inline Switch Distribution form — opened from the footer's
    // Switch-mode toggle link, replaces the right-column position
    // card with a Distribution field + Verify Ownership CTA. Local
    // staged value (`switchModeStaged`) lets the user reconsider
    // before committing; on Verify Ownership we copy it into
    // `manageDistributionMode` and close the form. Back cancels
    // without applying.
    const [isSwitchingMode, setIsSwitchingMode] = useState(false);
    const [switchModeStaged, setSwitchModeStaged] =
        useState<DistributionMode>("stream");
    const [
        canRetrySwitchWithMicroTransfer,
        setCanRetrySwitchWithMicroTransfer
    ] = useState(false);
    // Per-position routing surface — non-null when the user clicked
    // the "Destinations" link next to a breakdown row (USDv Balance /
    // Meteora DLMM / etc). Value is the source name; identifies
    // which position the form is scoped to. Per the routing research
    // (claude-docs/research/yaas-reward-routing-use-cases.md), each
    // position is its own dominion-and-control endpoint — this is
    // the per-position routing entry point recommended by Model E.
    const [routingPositionSource, setRoutingPositionSource] = useState<
        string | null
    >(null);
    useEffect(() => {
        if (!accountUnavailableStatus) return;
        setExploreOpen(false);
        setDistributionsOpen(false);
        setIsBuyingUSDv(false);
        setIsConfiguringRouting(false);
        setIsSwitchingMode(false);
        setRoutingPositionSource(null);
    }, [accountUnavailableStatus]);
    // Mode 2 (simple) per-position routing form — single-destination
    // label + address. Pre-fill the address with the mock connected
    // wallet so the form reads as a confirm-the-default; Mode 3 (the
    // splits UI) ignores these and uses its own state.
    const [simpleRoutingLabel, setSimpleRoutingLabel] = useState("");
    const [simpleRoutingAddress, setSimpleRoutingAddress] = useState(
        MOCK_CONNECTED_WALLET
    );
    // Shared destinations — lifted out of EarnContent so the address
    // book persists across surfaces (Set Destinations panel) and so
    // the per-position routing-edit view can render the same list
    // the user just added to. EarnContent gets the array + setter as
    // props (falls back to internal state if the props are omitted,
    // for standalone consumers).
    const [destinations, setDestinations] = useState<SharedDestination[]>([
        {
            id: "source",
            uuid: undefined,
            label: "Source Account",
            address: MOCK_CONNECTED_WALLET,
            percentage: 100,
            type: "wallet"
        }
    ]);
    useEffect(() => {
        setDestinations((prev) => {
            // Carry existing percentages over by ADDRESS (not uuid) so an
            // allocation survives the synthetic "Source Account" row being
            // replaced by a real seeded self saved-destination (same address).
            const previousPercentageByAddress = new Map(
                prev.map((destination) => [
                    destination.address,
                    destination.percentage
                ])
            );
            const sourceAddress =
                resolved.address || walletAddress || MOCK_CONNECTED_WALLET;
            const savedRows: SharedDestination[] =
                savedDestinations.destinations.map((destination) => ({
                    id: destination.uuid,
                    uuid: destination.uuid,
                    // A saved row for the connected wallet reads as "Same
                    // Wallet" rather than its stored name.
                    label:
                        destination.address === sourceAddress
                            ? "Same Wallet"
                            : destination.name,
                    address: destination.address,
                    percentage:
                        previousPercentageByAddress.get(destination.address) ??
                        0,
                    type: "wallet"
                }));
            // If a saved row already covers the connected wallet, it replaces
            // the local Source Account fallback so the address appears once.
            if (savedRows.some((row) => row.address === sourceAddress)) {
                return savedRows;
            }
            const normalizedSource: SharedDestination = {
                id: "source",
                uuid: undefined,
                label: "Source Account",
                address: sourceAddress,
                percentage:
                    previousPercentageByAddress.get(sourceAddress) ?? 100,
                type: "wallet"
            };
            return [normalizedSource, ...savedRows];
        });
    }, [resolved.address, savedDestinations.destinations, walletAddress]);
    // Advanced Mode toggle — gates Mode 3 features (address book +
    // multi-destination splits per position) vs Mode 2 (single
    // destination per position, no book). Defaults off so users land
    // in the simpler Mode 2 surface. Per the routing research doc
    // (Section 8), promoting users to Mode 3 should be deliberate.
    const [advancedModeRequested, setAdvancedMode] = useState(false);
    // `FEATURE_FLAGS.retailMultiDestinationRouting` is the hard gate;
    // the state above is the user's preference within it. Deriving the
    // effective value here (rather than gating each Mode 3 mount
    // separately) means every downstream consumer — the breakdown
    // list's edit affordance, the action row, the routing panel — sees
    // `false` from one place and cannot disagree about which mode the
    // page is in. With the flag off, `/yaas` is single-destination
    // only and there is no way to reach a split editor.
    const advancedMode =
        FEATURE_FLAGS.retailMultiDestinationRouting && advancedModeRequested;
    // Mode 2 inline accordion — which breakdown row's Update has been
    // clicked. Mode 3 falls through to `routingPositionSource` (full
    // surface swap) instead; Mode 2 expands the editor inline below
    // the row so the user keeps the rest of the page context.
    const [inlineEditPosition, setInlineEditPosition] = useState<string | null>(
        null
    );
    const routingPosition = routingPositionSource
        ? (resolved.positions.find(
              (position) => String(position.id) === routingPositionSource
          ) ?? null)
        : null;
    const routingPositionTitle = routingPosition
        ? breakdownTitleFor(
              routingPosition.pool,
              routingPosition.position.lpIdentity?.protocol
          )
        : routingPositionSource;
    const { refetch: refetchSavedDestinations } = savedDestinations;
    useEffect(() => {
        if (!advancedMode || !routingPositionSource) return;
        refetchSavedDestinations();
    }, [advancedMode, routingPositionSource, refetchSavedDestinations]);
    // Initialize the advanced grid's percentages from the position's CURRENT
    // routing, exactly once per editor-open. Guarded by a ref (rather than a
    // "has any percentage" heuristic) so it (a) still fires when saved
    // destinations finish loading AFTER the editor opened — the row that
    // represents the live destination may not exist yet on first run — and
    // (b) never stomps a user's in-progress edits when the saved list
    // changes. Resets when the editor closes.
    const initializedRoutingRef = useRef<string | null>(null);
    useEffect(() => {
        if (!routingPositionSource || !routingPosition) {
            initializedRoutingRef.current = null;
            return;
        }
        if (initializedRoutingRef.current === routingPositionSource) return;

        const sourceAddress =
            resolved.address || walletAddress || MOCK_CONNECTED_WALLET;
        const splits = routingPosition.splits;

        if (splits.length > 0) {
            // Splits routing — wait until every split's saved row has loaded so
            // we don't half-apply, then map each row's percentage.
            const allLoaded = splits.every((split) =>
                savedDestinations.destinations.some(
                    (d) => d.uuid === split.savedDestinationUuid
                )
            );
            if (!allLoaded) return;
            const splitPercentageByUuid = new Map(
                splits.map((split) => [
                    split.savedDestinationUuid,
                    split.percentageBps / 100
                ])
            );
            setDestinations((prev) =>
                prev.map((destination) => ({
                    ...destination,
                    percentage: destination.uuid
                        ? (splitPercentageByUuid.get(destination.uuid) ?? 0)
                        : 0
                }))
            );
            initializedRoutingRef.current = routingPositionSource;
            return;
        }

        // Single live destination → 100% on the row that represents it (a saved
        // row by address, or the synthetic self/source row).
        //
        // GAP: an external address that is not in the book has no row, so the
        // grid initializes to nothing and the position's live destination is
        // invisible here. The read-only "Current destination · 100%" row used
        // to cover this case; it was removed along with Save-to-address-book.
        // Init is left unmarked so it still applies if the address later
        // enters the book. Closing this properly means letting the user add a
        // destination from this screen.
        const destAddress = routingPosition.rewards.destination_address;
        const representable =
            destAddress === sourceAddress ||
            savedDestinations.destinations.some(
                (d) => d.address === destAddress
            );
        if (!representable) return;
        setDestinations((prev) =>
            prev.map((destination) => ({
                ...destination,
                percentage:
                    destination.address === destAddress ||
                    (destination.id === "source" &&
                        destAddress === sourceAddress)
                        ? 100
                        : 0
            }))
        );
        initializedRoutingRef.current = routingPositionSource;
    }, [
        routingPositionSource,
        routingPosition,
        savedDestinations.destinations,
        resolved.address,
        walletAddress
    ]);
    const createSavedDestination = async (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }): Promise<
        | { success: true; destination: SharedDestination }
        | { success: false; error: string }
    > => {
        const result = await savedDestinations.createDestination(input);
        if (!result.success) return { success: false, error: result.error };
        return { success: true, destination: toSharedDestination(result.data) };
    };
    // Real APY value once loaded; null while the reporting hook is still
    // in flight or the rate is unavailable. Every surface is loading-aware,
    // so none presents a made-up rate as live.
    const yaasApyOrNull = averageAPY ? parseFloat(averageAPY) : null;

    // WORKAROUND, remove when `/enroll` creates the Program Position.
    //
    // Enrollment ought to be "a Program Position exists" — that row is
    // the Program's own record of this wallet, and what yaas hands
    // Jachin. But `/enroll` writes only the ParticipantAccount and its
    // terms acceptance; the Position waits for the next execution
    // snapshot to reconcile, which is a 14-day distribution cycle. In
    // that window an enrolled wallet has no Position, and keying the
    // hero off one pitched "Your USDv can earn" at someone who had
    // already signed — while the right column said "Enabled".
    //
    // An Active account is the stand-in: enroll writes it in the same
    // transaction as the acceptance and refuses (409) to touch an
    // account in any other status, so Active is unreachable without a
    // matching signature. Once enroll creates the Position this
    // collapses to `hasPosition` and the second clause is dead.
    const isEnrolled =
        resolved.isWalletConnected && (hasPosition || !!rewardsEnabledAccount);
    const showPositionsSection = isEnrolled;
    const rewardsAnalytics = useProductJourney(
        "rewards",
        "rewards_entry",
        walletAddress || undefined
    );
    useProductObservation(rewardsAnalytics, "state_viewed", {
        status:
            accountUnavailableStatus ??
            (!isEnrolled
                ? "not_enrolled"
                : isPositionDisabled
                  ? "disabled"
                  : isBelowMinimum
                    ? "below_minimum"
                    : primaryPosition
                      ? primaryPosition.position.status.toLowerCase()
                      : "awaiting_position"),
        distribution_mode: manageDistributionMode,
        item_count: overlaidWalletPositions.length
    });
    useEffect(() => {
        // Mode 3 surfaces routing inside the Reward Destinations
        // panel, not inline on a per-row basis — collapse any open
        // Mode 2 inline editor when the user flips Advanced on so
        // they don't return to a stale expanded row later.
        if (advancedMode) setInlineEditPosition(null);
    }, [advancedMode]);

    useWalletHomeAnalytics({
        view: "retail",
        landing_variant:
            resolved.landingVariant === "swap-from-stables"
                ? "swap_from_stables"
                : resolved.landingVariant === "swap-no-stables"
                  ? "swap_no_stables"
                  : resolved.landingVariant === "below-min"
                    ? "below_minimum"
                    : resolved.landingVariant,
        wallet_connected: wallet.isWalletConnected,
        retail_view:
            flow.pageView === "not-enrolled"
                ? "not_enrolled"
                : flow.pageView === "rewards-enabled"
                  ? "rewards_enabled"
                  : flow.pageView === "account-unavailable"
                    ? "account_unavailable"
                    : flow.pageView,
        account_status:
            wallet.account?.status === "Active"
                ? "active"
                : wallet.account?.status === "Pending"
                  ? "pending"
                  : wallet.account?.status === "Suspended"
                    ? "suspended"
                    : wallet.account?.status === "Blacklisted"
                      ? "blacklisted"
                      : wallet.account?.status === "Closed"
                        ? "closed"
                        : "unknown",
        has_position: !!primaryPosition,
        position_disabled: isPositionDisabled,
        below_minimum: isFirstBalancesLoad ? undefined : isBelowMinimum
    });

    return (
        <main className="flex-1 flex flex-col md:flex-row bg-surface-ground overflow-hidden relative">
            <SimBadge simMode={flow.isSimulated ? flow.simMode : null} />
            <LeftHero
                resolved={resolved}
                isEnrolled={isEnrolled}
                averageAPY={averageAPY}
                distributionMode={manageDistributionMode}
                isBelowMinimum={!!isBelowMinimum}
                isDisabled={isPositionDisabled}
                walletBalance={usdvBalanceExact}
            />

            {/* Action container — visual right on desktop (natural
                row order), full-width on mobile. md:border-l places
                the divider between this panel and the guilloche on
                its left. Content swaps between the action stack
                (card + explore invitation) and the explorer chart
                with a page-fade-up transition keyed by
                `exploreOpen`. */}
            <div className="flex-1 md:flex-none md:w-[40%] md:border-l border-line-base flex flex-col items-center justify-start md:justify-center relative px-4 py-5 md:px-6 md:py-6">
                <div className="w-full max-w-md flex flex-col gap-6 md:mt-10">
                    {accountUnavailableStatus ? (
                        <AccountUnavailablePanel
                            status={accountUnavailableStatus}
                        />
                    ) : exploreOpen && showPositionsSection ? (
                        <div
                            key="explorer"
                            style={{
                                opacity: 0,
                                animation:
                                    "page-fade-up 350ms ease-out 0ms forwards"
                            }}
                        >
                            <RetailYaasComparisonExplorer
                                analytics={productJourneys.current.earn_compare}
                                walletAddress={walletAddress || undefined}
                                yaasApy={yaasApyOrNull}
                                historicalAprPct={
                                    !flow.isSimulated &&
                                    demoState === null &&
                                    wallet.account?.address ===
                                        selectedAccount.address
                                        ? (wallet.account?.historicalAprPct ??
                                          null)
                                        : null
                                }
                                usdvBalance={usdvBalance}
                                onClose={() => setExploreOpen(false)}
                            />
                        </div>
                    ) : distributionsOpen && resolved.isWalletConnected ? (
                        <div
                            key="distributions"
                            style={{
                                opacity: 0,
                                animation:
                                    "page-fade-up 350ms ease-out 0ms forwards"
                            }}
                        >
                            <AccountTransactionHistory
                                balanceAddress={
                                    selectedAccount.isSquads
                                        ? selectedAccount.vaultAddress
                                        : undefined
                                }
                                analytics={productJourneys.current.history}
                                walletAddress={resolved.address}
                                onClose={() => setDistributionsOpen(false)}
                            />
                        </div>
                    ) : (
                        <motion.div
                            // Action column wrapper (no `layout` prop on
                            // purpose — Framer's layout uses
                            // translate+scaleY which scales children,
                            // and we don't need cross-render bounds
                            // animation here anyway). Holds the
                            // AnimatePresence (action-card branches)
                            // and the always-mounted footer below it.
                            // The wrapper keeps one stable action-column width
                            // while the account view changes.
                            key="action"
                            className="flex flex-col"
                            style={{
                                opacity: 0,
                                animation:
                                    "page-fade-up 350ms ease-out 0ms forwards"
                            }}
                        >
                            {/* Action card switch — wrapped in
                                AnimatePresence so the four branches
                                crossfade into one another instead of
                                hard-cutting. mode="wait" lets the
                                outgoing branch finish exiting before
                                the next mounts so cards never stack
                                vertically (would shift layout). The
                                preview branch is keyed by connection
                                state so disconnected→connected
                                remounts the stack and replays its
                                border-trace + count-up entrance,
                                confirming the wallet connect to the
                                user. */}
                            <AnimatePresence mode="wait" initial={false}>
                                {hasPosition &&
                                FEATURE_FLAGS.retailPositionRouting &&
                                routingPositionSource ? (
                                    <PerPositionRoutingPanel
                                        analytics={
                                            productJourneys.current
                                                .rewards_routing
                                        }
                                        positionSource={
                                            routingPositionTitle ??
                                            routingPositionSource
                                        }
                                        advancedMode={advancedMode}
                                        destinations={destinations}
                                        setDestinations={setDestinations}
                                        simpleRoutingLabel={simpleRoutingLabel}
                                        setSimpleRoutingLabel={
                                            setSimpleRoutingLabel
                                        }
                                        simpleRoutingAddress={
                                            simpleRoutingAddress
                                        }
                                        setSimpleRoutingAddress={
                                            setSimpleRoutingAddress
                                        }
                                        connectedWallet={resolved.address}
                                        walletAddress={resolved.address}
                                        positionId={routingPosition?.id ?? null}
                                        onCreateDestination={
                                            createSavedDestination
                                        }
                                        isCreatingDestination={
                                            savedDestinations.isCreating
                                        }
                                        onClose={() =>
                                            setRoutingPositionSource(null)
                                        }
                                    />
                                ) : isSwitchingMode && showPositionsSection ? (
                                    <SwitchDistributionModePanel
                                        analytics={
                                            productJourneys.current
                                                .rewards_distribution
                                        }
                                        walletAddress={
                                            walletAddress || undefined
                                        }
                                        stagedMode={switchModeStaged}
                                        setStagedMode={(mode) => {
                                            setSwitchModeStaged(mode);
                                            setCanRetrySwitchWithMicroTransfer(
                                                false
                                            );
                                        }}
                                        committedMode={manageDistributionMode}
                                        onBack={() => {
                                            setCanRetrySwitchWithMicroTransfer(
                                                false
                                            );
                                            setIsSwitchingMode(false);
                                        }}
                                        onCommit={(
                                            analytics,
                                            preferredMethod
                                        ) => {
                                            setCanRetrySwitchWithMicroTransfer(
                                                false
                                            );
                                            // Sim / demo frames commit
                                            // locally; real mode runs the
                                            // wallet-signed ceremony and
                                            // only commits on success.
                                            if (
                                                !canSubmitRealActions ||
                                                !walletAddress
                                            ) {
                                                setManageDistributionMode(
                                                    switchModeStaged
                                                );
                                                setIsSwitchingMode(false);
                                                return;
                                            }
                                            void (async (): Promise<void> => {
                                                const result =
                                                    await distributionModeApi.setDistributionMode(
                                                        {
                                                            walletAddress,
                                                            analytics,
                                                            distributionMode:
                                                                switchModeStaged,
                                                            preferredMethod
                                                        }
                                                    );
                                                if (result.success) {
                                                    analytics.close(
                                                        "completed"
                                                    );
                                                    setManageDistributionMode(
                                                        switchModeStaged
                                                    );
                                                    setIsSwitchingMode(false);
                                                } else {
                                                    setCanRetrySwitchWithMicroTransfer(
                                                        result.canRetryWithMicroTransfer
                                                    );
                                                }
                                            })();
                                        }}
                                        canRetryWithMicroTransfer={
                                            canRetrySwitchWithMicroTransfer
                                        }
                                        isCommitting={
                                            distributionModeApi.isLoading
                                        }
                                        commitError={distributionModeApi.error}
                                    />
                                ) : hasPosition &&
                                  FEATURE_FLAGS.retailMultiDestinationRouting &&
                                  isConfiguringRouting ? (
                                    /* The Reward Destinations panel —
                                       address book + split percentage
                                       table — is multi-destination by
                                       definition. Its only entry-point
                                       (the action row's Saved
                                       Destinations link) is already
                                       flag-gated; this second guard
                                       keeps the surface unmountable
                                       even if a future caller sets
                                       `isConfiguringRouting` directly. */
                                    <ConfigureRoutingPanel
                                        analytics={
                                            productJourneys.current
                                                .saved_destination
                                        }
                                        destinations={destinations}
                                        onDestinationsChange={setDestinations}
                                        connectedWallet={resolved.address}
                                        onCreateDestination={
                                            createSavedDestination
                                        }
                                        isCreatingDestination={
                                            savedDestinations.isCreating
                                        }
                                        onBack={() =>
                                            setIsConfiguringRouting(false)
                                        }
                                    />
                                ) : isBuyingUSDv ? (
                                    <BuyUsdvPanel
                                        analytics={productJourneys.current.swap}
                                        isWalletConnected={
                                            resolved.isWalletConnected
                                        }
                                        onBack={() => setIsBuyingUSDv(false)}
                                        onSwapComplete={handleSwapComplete}
                                    />
                                ) : showPositionsSection ? (
                                    <PositionsSection
                                        squads={selectedAccount.isSquads}
                                        balanceAddress={
                                            selectedAccount.isSquads
                                                ? selectedAccount.vaultAddress
                                                : undefined
                                        }
                                        balanceError={lpHoldings.error}
                                        onRetryBalance={() =>
                                            void lpHoldings.refetch()
                                        }
                                        retryingBalance={lpHoldings.isFetching}
                                        balanceHoldings={lpHoldings.holdings}
                                        balanceTotal={
                                            flow.isSimulated ||
                                            demoState !== null
                                                ? demoBalanceTotal(
                                                      resolved.positions
                                                  )
                                                : balanceTotal
                                        }
                                        distributionMode={
                                            manageDistributionMode
                                        }
                                        isBelowMinimum={!!isBelowMinimum}
                                        resolved={resolved}
                                        primaryPosition={primaryPosition}
                                        account={
                                            rewardsEnabledAccount ??
                                            resolved.account
                                        }
                                        formattedWalletBalance={
                                            isFirstBalancesLoad
                                                ? "—"
                                                : formatCurrency(usdvBalance)
                                        }
                                        userDisabledOverride={
                                            isPositionDisabled
                                        }
                                        advancedMode={advancedMode}
                                        canEditDestinations={
                                            canEditDestinations
                                        }
                                        canSubmitDestinations={
                                            canSubmitDestinations
                                        }
                                        inlineEditPosition={inlineEditPosition}
                                        baseApy={yaasApyOrNull}
                                        setInlineEditPosition={
                                            setInlineEditPosition
                                        }
                                        onConfigureDestinations={(
                                            source,
                                            analytics
                                        ) => {
                                            productJourneys.current.rewards_routing =
                                                analytics;
                                            setRoutingPositionSource(source);
                                        }}
                                        onBuyUsdv={handleBuyUSDv}
                                        onSwapUsdv={handleBuyUSDv}
                                        onConfigureRouting={() => {
                                            requestProductFlow(
                                                "saved_destination",
                                                "rewards_footer"
                                            );
                                            setIsConfiguringRouting(true);
                                        }}
                                    />
                                ) : flow.pageView === "enrolling" ? (
                                    <EnableRewardsForm
                                        flow={flow}
                                        connectedWallet={
                                            selectedAccount.isSquads
                                                ? selectedAccount.vaultAddress
                                                : resolved.address
                                        }
                                        signerAddress={
                                            selectedAccount.isSquads
                                                ? selectedAccount.signerAddress
                                                : undefined
                                        }
                                        eligibilityNotice={eligibilityNotice}
                                    />
                                ) : resolved.isPreviewMode ? (
                                    <PreviewLanding
                                        resolved={resolved}
                                        isSquads={selectedAccount.isSquads}
                                        balanceError={
                                            balanceError ||
                                            lpHoldings.error?.message ||
                                            wallet.error?.message
                                        }
                                        onRetryBalance={() => {
                                            void retryBalances();
                                            void lpHoldings.refetch();
                                            wallet.refetch();
                                        }}
                                        onSwapComplete={handleSwapComplete}
                                        yaasApy={yaasApyOrNull}
                                        // The form shows a failed live
                                        // eligibility check and blocks
                                        // submit until it clears.
                                        onEnable={flow.startEnrollment}
                                        onGetMoreUsdv={handleBuyUSDv}
                                        onBuyUsdv={handleBuyUSDv}
                                        flow={flow}
                                    />
                                ) : null}
                            </AnimatePresence>
                            {/* Account actions are available immediately after enrollment. */}
                            {!isConfiguringRouting &&
                                !isSwitchingMode &&
                                !accountUnavailableStatus &&
                                showPositionsSection &&
                                !routingPositionSource && (
                                    <YaasPositionsFooter
                                        yaasApy={yaasApyOrNull}
                                        advancedRouting={advancedMode}
                                        // Omitted — not disabled — when
                                        // multi-destination routing is
                                        // flagged off: the footer drops
                                        // the link entirely rather than
                                        // advertising a mode this build
                                        // cannot enter.
                                        onSwitchRoutingMode={
                                            hasPosition &&
                                            !selectedAccount.isSquads &&
                                            FEATURE_FLAGS.retailMultiDestinationRouting
                                                ? (): void => {
                                                      productEvent(
                                                          "rewards_routing:changed",
                                                          {
                                                              entry_point:
                                                                  "rewards_footer",
                                                              advanced:
                                                                  !advancedMode
                                                          }
                                                      );
                                                      setAdvancedMode(
                                                          !advancedMode
                                                      );
                                                  }
                                                : undefined
                                        }
                                        cardSettled
                                        currentDistributionMode={
                                            manageDistributionMode
                                        }
                                        // Omitted while the Stream / Claim
                                        // choice is flagged off, same as
                                        // the routing link above.
                                        onSwitchDistributionMode={
                                            FEATURE_FLAGS.retailDistributionModeChoice &&
                                            !selectedAccount.isSquads
                                                ? (): void => {
                                                      // Pre-stage the OPPOSITE mode
                                                      // — the link's copy tells the
                                                      // user what they're switching
                                                      // TO, so opening the form
                                                      // already selected on that
                                                      // target lines the form up
                                                      // with the user's stated
                                                      // intent. They can still
                                                      // re-toggle inside the form
                                                      // before verifying.
                                                      setSwitchModeStaged(
                                                          manageDistributionMode ===
                                                              "stream"
                                                              ? "claim"
                                                              : "stream"
                                                      );
                                                      setCanRetrySwitchWithMicroTransfer(
                                                          false
                                                      );
                                                      requestProductFlow(
                                                          "rewards_distribution",
                                                          "rewards_footer"
                                                      );
                                                      setIsSwitchingMode(true);
                                                  }
                                                : undefined
                                        }
                                        onOpenExplore={() => {
                                            requestProductFlow(
                                                "earn_compare",
                                                "rewards_footer"
                                            );
                                            setExploreOpen(true);
                                        }}
                                        onOpenTransactions={() => {
                                            requestProductFlow(
                                                "history",
                                                "rewards_footer"
                                            );
                                            setDistributionsOpen(true);
                                        }}
                                    />
                                )}
                        </motion.div>
                    )}
                </div>
            </div>
            {/* Mobile footnote — mirrors USDvPage's mobile footer.
                Hidden on desktop because the left guilloche panel
                (USDvLandingPanel) renders its own footnote. */}
            <AppFootnote className="md:hidden px-6 mt-6 pb-3" />
            <VerificationConsentModal />
        </main>
    );
}

export default RetailYaasLandingPage;
